import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  FileText, Upload, Wallet, TrendingUp, AlertCircle, CheckCircle2, ArrowRight,
  Calculator, Sparkles, ChevronRight, ExternalLink, RefreshCw, IndianRupee,
} from "lucide-react";
import { inr } from "@/lib/format";
import AppShell from "@/components/AppShell";
import MetricCard from "@/components/business/MetricCard";
import RecommendationCard from "@/components/business/RecommendationCard";
import EmptyState from "@/components/business/EmptyState";

const StatusPill = ({ status }) => {
  const map = {
    draft: { label: "Draft", cls: "bg-amber-500/10 text-amber-700 dark:text-amber-400" },
    prepared: { label: "Prepared — awaiting Government submission", cls: "bg-blue-500/10 text-blue-700 dark:text-blue-400" },
    awaiting_user_submission: { label: "Ready to upload at incometax.gov.in", cls: "bg-blue-500/10 text-blue-700 dark:text-blue-400" },
    ca_requested: { label: "CA assistance requested", cls: "bg-violet-500/10 text-violet-700 dark:text-violet-400" },
    ca_in_review: { label: "Under CA review", cls: "bg-violet-500/10 text-violet-700 dark:text-violet-400" },
    ca_docs_pending: { label: "Awaiting your documents", cls: "bg-amber-500/10 text-amber-700 dark:text-amber-400" },
    ca_filing: { label: "Being filed by CA", cls: "bg-violet-500/10 text-violet-700 dark:text-violet-400" },
    filed_with_government: { label: "Filed with Income Tax Dept.", cls: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" },
  };
  const m = map[status] || { label: status, cls: "bg-muted text-muted-foreground" };
  return <span className={`text-xs font-semibold px-2 py-0.5 rounded ${m.cls}`}>{m.label}</span>;
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [filings, setFilings] = useState([]);
  const [nsdlStatus, setNsdlStatus] = useState(null);
  const [nsdlLoading, setNsdlLoading] = useState(false);

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data)).catch(() => {});
    api.get("/filings").then(({ data }) => setFilings(data)).catch(() => {});
  }, []);

  const fetchNsdlStatus = async () => {
    setNsdlLoading(true);
    try {
      const { data: s } = await api.get("/tax/refund-status");
      setNsdlStatus(s);
    } catch (err) {
      setNsdlStatus({ status: err?.response?.data?.detail || "Could not fetch — check your PAN in profile." });
    } finally {
      setNsdlLoading(false);
    }
  };

  const stats = data?.stats || {};
  const u = data?.user || user;
  const currentAY = data?.current_ay || "2026-27";

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Assessment Year {currentAY}</div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">
              Hi {u?.full_name?.split(" ")[0] || "there"} 👋
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">Here's your tax picture at a glance.</p>
          </div>
          <div className="flex gap-2">
            <Button asChild data-testid="dash-start-filing"><Link to="/filing"><FileText className="h-4 w-4 mr-2" />Continue filing</Link></Button>
            <Button asChild variant="outline"><Link to="/calculator"><Calculator className="h-4 w-4 mr-2" />Calculator</Link></Button>
          </div>
        </div>

        {/* PROFILE COMPLETION */}
        {u?.profile_completion < 100 && (
          <Card className="mt-6 p-5 border-border">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="text-sm font-semibold">Complete your profile</div>
                <div className="text-xs text-muted-foreground mt-0.5">Add PAN and Aadhaar to unlock e-verification.</div>
              </div>
              <div className="flex items-center gap-3 min-w-[260px]">
                <Progress value={u?.profile_completion || 0} className="h-2 flex-1" />
                <div className="text-sm font-semibold w-10 text-right num-rupee">{u?.profile_completion || 0}%</div>
                <Button asChild size="sm" variant="outline"><Link to="/profile">Edit</Link></Button>
              </div>
            </div>
          </Card>
        )}

        {/* CONTROL ROOM GRID */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-4 gap-5">
          <MetricCard
            testId="kpi-itr-status"
            label="ITR Status"
            icon={FileText}
            value={data?.latest_filing ? <StatusPill status={data.latest_filing.status} /> : "Not started"}
            helper={data?.latest_filing ? `${data.latest_filing.itr_type} · AY ${data.latest_filing.assessment_year}` : `Begin your AY ${currentAY} return`}
          />

          <MetricCard
            testId="kpi-tax-liability"
            label={data?.demand > 0 ? "Tax Demand" : "Tax Liability"}
            icon={data?.demand > 0 ? IndianRupee : Wallet}
            value={
              <span className={`num-rupee ${data?.demand > 0 ? "text-rose-600" : ""}`}>
                {data?.demand > 0
                  ? inr(data.demand)
                  : data?.tax_liability != null
                    ? inr(data.tax_liability)
                    : "—"}
              </span>
            }
            helper={data?.demand > 0 ? "Outstanding — pay before filing" : "Based on your latest draft"}
          />

          <MetricCard
            testId="kpi-refund"
            label={data?.refund_due > 0 ? "Refund Due" : "Filing Status"}
            icon={TrendingUp}
            value={
              data?.refund_due > 0
                ? <span className="num-rupee text-emerald-600">{inr(data.refund_due)}</span>
                : <span className="text-sm">{data?.refund_status || "—"}</span>
            }
            helper={data?.refund_due > 0 ? "Expected after filing + e-verify" : "Current filing progress"}
            tone="accent"
          />

          <MetricCard
            testId="kpi-documents"
            label="Documents"
            icon={Upload}
            value={<span className="num-rupee">{stats.documents || 0}</span>}
            helper={`${stats.unread_notifications || 0} new notifications`}
          />
        </div>

        {/* e-Pay Tax banner — only visible when demand exists */}
        {data?.demand > 0 && data?.epay_link && (
          <Card className="mt-6 p-5 border-rose-300 bg-rose-50 dark:bg-rose-950/30 dark:border-rose-800">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-rose-700 dark:text-rose-400">
                    Tax demand of {inr(data.demand)} outstanding for AY {currentAY}
                  </div>
                  <div className="text-xs text-rose-600/80 dark:text-rose-400/70 mt-0.5">
                    Pay via the Income Tax portal before the filing deadline to avoid interest u/s 234B.
                  </div>
                </div>
              </div>
              <Button asChild size="sm" className="bg-rose-600 hover:bg-rose-700 text-white shrink-0">
                <a href={data.epay_link} target="_blank" rel="noopener noreferrer">
                  Pay now at incometax.gov.in <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
                </a>
              </Button>
            </div>
          </Card>
        )}

        {/* AIS / 26AS */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-5">
          <Card className="p-6 border-border lg:col-span-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">AIS Summary · Annual Information Statement</div>
                <div className="font-display text-lg font-semibold mt-1">Reconciled with Form 26AS</div>
              </div>
              <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">Estimated from filing</Badge>
            </div>
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-4">
              <KV label="Salary income" val={inr(data?.ais?.salary_total)} />
              <KV label="Interest income" val={inr(data?.ais?.interest_total)} />
              <KV label="Dividend income" val={inr(data?.ais?.dividend_total)} />
              <KV label="TDS total" val={inr(data?.ais?.tds_total)} />
              <KV label="SFT entries" val={data?.ais?.sft_entries || 0} />
              <KV label="Advance tax (26AS)" val={inr(data?.form_26as?.advance_tax)} />
            </div>
            <div className="mt-5 pt-4 border-t border-border flex items-center justify-between gap-3 flex-wrap">
              <div className="text-xs text-muted-foreground flex items-center gap-2">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                Figures from your filing data. Direct AIS import from IT Dept coming with ERI integration.
              </div>
              <a href="https://eportal.incometax.gov.in/iec/foservices/#/ais" target="_blank" rel="noopener noreferrer"
                className="text-xs text-accent hover:underline flex items-center gap-1 shrink-0">
                View on IT portal <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </Card>

          <div className="flex flex-col gap-5">
            <Card className="p-6 border-border flex-1">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Pending tasks</div>
              <ul className="mt-4 space-y-3" data-testid="pending-tasks">
                {(data?.pending_tasks || []).length === 0 && (
                  <li className="text-sm text-muted-foreground flex gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /> Nothing pending. Great work.</li>
                )}
                {(data?.pending_tasks || []).map((t) => (
                  <li key={t.id} className="flex items-start gap-3">
                    <div className={`mt-1 h-2 w-2 rounded-full ${t.priority === "high" ? "bg-rose-500" : "bg-amber-500"}`} />
                    <div className="flex-1 text-sm">{t.title}</div>
                  </li>
                ))}
              </ul>
            </Card>

            {/* NSDL Refund Status */}
            <Card className="p-6 border-border">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Refund Status · NSDL</div>
                <button onClick={fetchNsdlStatus} disabled={nsdlLoading}
                  className="text-xs text-accent hover:underline flex items-center gap-1 disabled:opacity-50">
                  <RefreshCw className={`h-3 w-3 ${nsdlLoading ? "animate-spin" : ""}`} />
                  {nsdlLoading ? "Fetching…" : "Check now"}
                </button>
              </div>
              {nsdlStatus ? (
                <div className="text-sm">
                  <div className={`font-semibold ${nsdlStatus.status?.toLowerCase().includes("no refund") || nsdlStatus.status === "Unavailable" ? "text-muted-foreground" : "text-emerald-600"}`}>
                    {nsdlStatus.status}
                  </div>
                  {nsdlStatus.amount && <div className="mt-1 num-rupee text-lg font-bold">{inr(nsdlStatus.amount)}</div>}
                  {nsdlStatus.details && nsdlStatus.source === "fallback" && (
                    <a href={nsdlStatus.check_url} target="_blank" rel="noopener noreferrer"
                      className="mt-2 text-xs text-accent hover:underline flex items-center gap-1">
                      Check directly on NSDL <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">
                  Click "Check now" to fetch your refund status from NSDL with your PAN.
                </div>
              )}
            </Card>
          </div>
        </div>

        {/* Recent filings */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-5">
          <Card className="p-6 border-border lg:col-span-2" data-testid="recent-filings">
            <div className="flex items-center justify-between">
              <div className="font-display text-lg font-semibold">Recent filings</div>
              <Button asChild variant="ghost" size="sm"><Link to="/filing">New filing <ChevronRight className="ml-1 h-4 w-4" /></Link></Button>
            </div>
            <div className="mt-4 divide-y divide-border">
              {filings.length === 0 && (
                <EmptyState
                  icon={FileText}
                  title="No filings yet."
                  description={`Start your AY ${currentAY} return and track it here.`}
                  action={{ label: "Start filing", onClick: () => navigate("/filing") }}
                  className="py-6"
                />
              )}
              {filings.map((f) => (
                <Link key={f.id} to={`/filing?id=${f.id}`} className="flex items-center justify-between py-3 hover:bg-muted/40 -mx-2 px-2 rounded">
                  <div>
                    <div className="text-sm font-semibold">{f.itr_type} · AY {f.assessment_year}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Regime: {f.regime} · Step: {f.current_step}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusPill status={f.status} />
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </Link>
              ))}
            </div>
          </Card>

          <RecommendationCard
            icon={Sparkles}
            title="Talk to Ritika — AI Tax Assistant"
            description="Ask anything about deductions, regime, ITR forms or notices. Trained on the entire Indian Income Tax Act."
            actionLabel="Open assistant"
            actionHref="/ai"
            actionIcon={ArrowRight}
          />
        </div>
      </div>
    </AppShell>
  );
}

function KV({ label, val }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className="mt-1 num-rupee font-display text-lg font-bold tracking-tight">{val}</div>
    </div>
  );
}
